import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface OptimizedQueryOptions<T> extends Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'> {
  queryKey: string[];
  queryFn: () => Promise<T>;
}

// Optimized query hook with longer cache times and intelligent stale-while-revalidate
export const useOptimizedQuery = <T>(options: OptimizedQueryOptions<T>) => {
  return useQuery({
    ...options,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Reduce unnecessary network calls
    refetchOnReconnect: 'always', // Only refetch on actual reconnects
  });
};

// Helper function for Supabase queries with built-in error handling
export const createSupabaseQuery = <T>(
  queryBuilder: () => any,
  transform?: (data: any) => T
) => {
  return async (): Promise<T> => {
    const { data, error } = await queryBuilder();
    
    if (error) {
      throw error;
    }
    
    return transform ? transform(data) : data;
  };
};